import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { form, required } from '@angular/forms/signals';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import type { GeoJSONSource, Map as MapboxMap, MapMouseEvent, LngLatBoundsLike } from 'mapbox-gl';
import { Area, ZipRange } from '@tourfold/libraries/tf-api-client-public-v2';
import { GeocodingV2Api } from '@tourfold/libraries/tf-api-client-frontend-v2';
import { MapboxMapComponent } from '@tourfold/libraries/shared-ui';
import { extractServerErrors, ServerError } from '@tourfold/libraries/shared-util';
import {
  AreaFormModel,
  AreaFormResult,
  DEFAULT_AREA_COLOR,
  MAX_AREA_DESCRIPTION_LENGTH,
  MAX_AREA_NAME_LENGTH,
  MAX_AREA_UPLOAD_BYTES,
  PRESET_COLORS,
} from './area-form-dialog.types';
import { CountryOption, listCountryOptions, resolveCountryIso3 } from './country-iso3';
import {
  GeoJsonFeature,
  GeoJsonValidationError,
  buildPolygonFeatureFromVertices,
  isGeoJsonValidationError,
  parseAreaGeoJsonString,
} from './geo-json.utils';

type Tab = 'draw' | 'upload';
type DrawMode = 'add' | 'edit';

interface UploadedFile {
  readonly name: string;
  readonly size: number;
}

@Component({
  selector: 'tf-settings-area-form-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TextareaModule,
    TooltipModule,
    TranslateModule,
    MapboxMapComponent,
  ],
  templateUrl: './area-form-dialog.html',
  styleUrl: './area-form-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AreaFormDialog {
  private readonly translateService = inject(TranslateService);
  // Optional so tests that don't render the dialog don't have to provide it. In production
  // the API is bound globally by web-api-clients.providers.ts.
  private readonly geocodingApi = inject(GeocodingV2Api, { optional: true });

  readonly visible = input(false);
  readonly editingArea = input<Area | null>(null);
  readonly saving = input(false);

  readonly visibleChange = output<boolean>();
  readonly cancelled = output<void>();
  readonly save = output<AreaFormResult>();

  readonly activeTab = signal<Tab>('draw');
  readonly drawMode = signal<DrawMode>('add');
  readonly drawnVertices = signal<readonly [number, number][]>([]);
  readonly uploadedFeature = signal<GeoJsonFeature | null>(null);
  readonly uploadedFile = signal<UploadedFile | null>(null);
  readonly uploadError = signal<GeoJsonValidationError | null>(null);
  readonly isDragOver = signal(false);
  readonly advancedOpen = signal(false);

  readonly formModel = signal<AreaFormModel>({
    name: '',
    description: '',
    color: DEFAULT_AREA_COLOR,
    zipRanges: [],
  });

  readonly areaForm = form(this.formModel, (schema) => {
    required(schema.name, { message: 'SETTINGS_AREAS.FORM.NAME_REQUIRED' });
  });

  /**
   * Inline server-error state. Keyed by field name (`name`, `description`, `color`,
   * `geometry`, `zipRanges`). Anything that doesn't map to one of those fields lands in
   * `general[]` and is returned to the caller so they can surface it some other way
   * (toast, page-level alert). Errors clear automatically the moment the user edits the
   * matching field — see the constructor's auto-clear effects.
   *
   * This replaces the previous `SignalFormServerErrors` integration because the backend
   * emits per-index pointers like `/zip_ranges[0].country` that the generic router can't
   * bucket — and because the geometry "field" lives outside `formModel`, so the standard
   * `keyof T` typing wouldn't let us register it.
   */
  private readonly serverFieldErrors = signal<Record<string, string>>({});
  /** Backwards-compatible getter the template uses: `serverErrors.get('name')`. */
  readonly serverErrors = {
    get: (field: string): string | undefined => this.serverFieldErrors()[field],
  };
  readonly geometryServerError = computed(() => this.serverFieldErrors()['geometry'] ?? null);
  readonly zipRangesServerError = computed(() => this.serverFieldErrors()['zipRanges'] ?? null);

  readonly presetColors = PRESET_COLORS;
  readonly maxNameLength = MAX_AREA_NAME_LENGTH;
  readonly maxDescriptionLength = MAX_AREA_DESCRIPTION_LENGTH;

  readonly previewFeature = computed<GeoJsonFeature | null>(() => {
    const drawn = this.drawnVertices();
    if (drawn.length >= 3) return buildPolygonFeatureFromVertices(drawn);
    return this.uploadedFeature();
  });

  readonly hasGeometry = computed(() => this.previewFeature() !== null);

  readonly dialogHeader = computed(() =>
    this.editingArea()
      ? this.translateService.instant('SETTINGS_AREAS.DIALOG.EDIT_TITLE')
      : this.translateService.instant('SETTINGS_AREAS.DIALOG.CREATE_TITLE')
  );

  readonly primarySubmitLabel = computed(() =>
    this.editingArea()
      ? this.translateService.instant('SETTINGS_AREAS.ACTIONS.UPDATE')
      : this.translateService.instant('SETTINGS_AREAS.ACTIONS.SAVE')
  );

  /** "10 points placed · drawing…" / "Polygon ready" badge inside the map workspace. */
  readonly mapBadgeText = computed(() => {
    const drawn = this.drawnVertices();
    if (drawn.length > 0) {
      return this.translateService.instant('SETTINGS_AREAS.DIALOG.POINTS_PLACED', {
        count: drawn.length,
      });
    }
    if (this.uploadedFeature()) {
      return this.translateService.instant('SETTINGS_AREAS.DIALOG.POLYGON_READY');
    }
    return '';
  });

  readonly mapBadgeSubtext = computed(() => {
    if (this.drawnVertices().length > 0) {
      return this.translateService.instant('SETTINGS_AREAS.DIALOG.DRAWING');
    }
    return '';
  });

  /**
   * GeoJSON object handed to mapbox via `addSource('area', { type: 'geojson', data })`. Recomputes
   * whenever the user adds a vertex or completes an upload, then a small effect pushes it through.
   */
  readonly mapboxSourceData = computed<
    GeoJsonFeature | { type: 'FeatureCollection'; features: [] }
  >(() => this.previewFeature() ?? { type: 'FeatureCollection', features: [] });

  /** Vertex feature collection rendered as halo circles on top of the polygon. */
  readonly vertexSourceData = computed<{ type: 'FeatureCollection'; features: object[] }>(() => {
    const drawn = this.drawnVertices();
    return {
      type: 'FeatureCollection',
      features: drawn.map((position, index) => ({
        type: 'Feature',
        properties: { index, first: index === 0 },
        geometry: { type: 'Point', coordinates: position },
      })),
    };
  });

  private mapInstance: MapboxMap | null = null;
  private mapboxClickListener?: (event: MapMouseEvent) => void;
  private mapboxDoubleClickListener?: (event: MapMouseEvent) => void;
  private mapboxMouseDownListener?: (event: MapMouseEvent) => void;
  private dragVertexIndex: number | null = null;
  readonly mapboxComponent = viewChild(MapboxMapComponent);

  // ── Zip-range editor state (Advanced) ────────────────────────────────
  // ISO-3 country derived from the polygon's centroid via reverse geocoding. Stays null
  // until the user has a polygon (drawn or uploaded) AND the geocoder maps the returned
  // country name to an ISO-3 code (see resolveCountryIso3). Used as the *default* country
  // for newly added zip-range rows — users can still override per row.
  readonly derivedCountryIso3 = signal<string | null>(null);
  // Options for the country dropdown rendered next to each zip-range row. Computed lazily
  // and re-evaluated when the translate service's current language changes so labels stay
  // in the user's locale. Falls back to a default ISO-3 list if the platform's
  // `Intl.DisplayNames` rejects the locale (older WebKit, etc.).
  // PrimeNG's p-select types `options` as a mutable `any[]`; spread the readonly helper
  // result to satisfy that signature without exposing the underlying array for mutation.
  readonly countryOptions = computed<CountryOption[]>(() => [
    ...listCountryOptions(this.translateService.getCurrentLang() || 'en'),
  ]);
  readonly geocodingCentroid = signal<boolean>(false);
  private lastGeocodedCentroid: { lat: number; lon: number } | null = null;

  constructor() {
    // When the dialog is closed (`visible` flips to false) reset the form so the next open
    // is a fresh canvas. When it opens with an `editingArea` populate from it. Also nudge the
    // map to resize after the open transition so its gl canvas doesn't stay at 0×0 — the
    // map.load callback runs while the p-dialog is still animating in, which is why tiles
    // never paint without this.
    effect(() => {
      const isOpen = this.visible();
      if (!isOpen) return;
      const editing = this.editingArea();
      if (editing) {
        this.hydrateFromArea(editing);
      } else {
        this.resetForm();
      }
      const map = this.mapInstance;
      if (map) {
        // 200ms covers PrimeNG's default show transition; resize after it ends.
        setTimeout(() => map.resize(), 220);
      }
    });

    // Push the in-progress / uploaded polygon onto the live Mapbox map. Split into two
    // effects so that *every* signal we depend on (geometry, vertices, color) is
    // tracked unconditionally — the previous single-effect version was reading
    // formModel().color past an early return, so the first pass (before `mapInstance`
    // was bound) didn't register color as a dependency. Result: changing the colour
    // after an upload had no effect on the rendered polygon until the geometry changed
    // again. Splitting also gives us cheaper updates: a colour change only touches
    // paint properties, not source data.
    effect(() => {
      const data = this.mapboxSourceData();
      const vertices = this.vertexSourceData();
      this.applyDataToMap(data, vertices);
    });
    effect(() => {
      const color = this.formModel().color;
      this.applyColorToMap(color);
    });

    // Auto-clear inline server errors as soon as the user edits the matching subject.
    // Tracks each field's last value so the first effect tick (which runs on construction)
    // doesn't wipe a freshly-applied error before the user even sees it.
    let prevName = this.formModel().name;
    let prevDescription = this.formModel().description;
    let prevColor = this.formModel().color;
    let prevZipRanges = this.formModel().zipRanges;
    effect(() => {
      const m = this.formModel();
      if (m.name !== prevName) {
        prevName = m.name;
        this.clearServerFieldError('name');
      }
      if (m.description !== prevDescription) {
        prevDescription = m.description;
        this.clearServerFieldError('description');
      }
      if (m.color !== prevColor) {
        prevColor = m.color;
        this.clearServerFieldError('color');
      }
      if (m.zipRanges !== prevZipRanges) {
        prevZipRanges = m.zipRanges;
        this.clearServerFieldError('zipRanges');
      }
    });
    let prevVertices = this.drawnVertices();
    let prevUploaded = this.uploadedFeature();
    effect(() => {
      const v = this.drawnVertices();
      const u = this.uploadedFeature();
      if (v !== prevVertices || u !== prevUploaded) {
        prevVertices = v;
        prevUploaded = u;
        this.clearServerFieldError('geometry');
      }
    });

    // Reverse-geocode the polygon centroid whenever the geometry changes enough to matter.
    // We don't wait for the user to "finish" drawing — a 3-vertex polygon is enough to put
    // the centroid in the right country. The lastGeocodedCentroid threshold (≈11 m at the
    // equator) prevents repeated API calls when the centroid barely moves between clicks.
    effect(() => {
      const feature = this.previewFeature();
      if (!feature) {
        this.derivedCountryIso3.set(null);
        this.lastGeocodedCentroid = null;
        return;
      }
      const centroid = polygonCentroid(feature);
      if (!centroid) return;
      const previous = this.lastGeocodedCentroid;
      if (
        previous &&
        Math.abs(previous.lat - centroid.lat) < 1e-3 &&
        Math.abs(previous.lon - centroid.lon) < 1e-3
      ) {
        // Within ~100 m at the equator — skip; the country won't have changed.
        return;
      }
      this.lastGeocodedCentroid = centroid;
      void this.resolveCountryAt(centroid);
    });
  }

  /**
   * Calls the configured geocoding provider for the centroid and maps the returned
   * (English) country name to an ISO-3 code. Failures (network, unmappable name, no API
   * configured) leave `derivedCountryIso3` at null, which prevents new zip ranges from
   * being added until we have a real country.
   */
  private async resolveCountryAt(centroid: { lat: number; lon: number }): Promise<void> {
    if (!this.geocodingApi) return;
    this.geocodingCentroid.set(true);
    try {
      const address = await this.geocodingApi.reverseSearch({
        lat: centroid.lat,
        lon: centroid.lon,
      });
      const iso3 = resolveCountryIso3(address.country);
      this.derivedCountryIso3.set(iso3);
    } catch {
      this.derivedCountryIso3.set(null);
    } finally {
      this.geocodingCentroid.set(false);
    }
  }

  /**
   * Pulls all RFC 9457 errors out of the failed-save response and routes each to the
   * matching inline field. The caller (areas-list.ts) checks `hasFieldErrors` to decide
   * whether a toast is still warranted on top of the inline messages.
   *
   * Pointer routing follows the backend's `field` parameter on `ProblemException.fieldValidation`:
   *   - `/name` / `/title` → name input
   *   - `/description` → description textarea
   *   - `/color` → color input
   *   - `/geo_json` / `/geoJson` → "geometry" — rendered under the map workspace
   *   - `/zip_ranges*` / `/zipRanges*` → "zipRanges" — rendered under the zip-chip row
   * Anything else surfaces as a general message and the caller can choose what to do with it.
   */
  async applyServerError(
    error: unknown
  ): Promise<{ readonly generalMessages: readonly string[]; readonly hasFieldErrors: boolean }> {
    const parsed = await extractServerErrors(error);
    if (parsed.length === 0) {
      this.serverFieldErrors.set({});
      return { generalMessages: [], hasFieldErrors: false };
    }
    const fieldErrors: Record<string, string> = {};
    const general: string[] = [];
    for (const e of parsed) {
      const field = routePointer(e.pointer);
      const message = pickMessage(e);
      if (field) {
        // Last write wins per field — backend rarely emits multiple errors for the same
        // pointer, and when it does the later one is usually the more specific.
        if (message) fieldErrors[field] = message;
      } else if (message) {
        general.push(message);
      }
    }
    this.serverFieldErrors.set(fieldErrors);
    return {
      generalMessages: general,
      hasFieldErrors: Object.keys(fieldErrors).length > 0,
    };
  }

  /** Clears any current server-side error tied to `field`. Idempotent. */
  private clearServerFieldError(field: string): void {
    const current = this.serverFieldErrors();
    if (!current[field]) return;
    const next = { ...current };
    delete next[field];
    this.serverFieldErrors.set(next);
  }

  // ─── Form field helpers ────────────────────────────────────────────────
  pickColor(color: string): void {
    this.formModel.update((m) => ({ ...m, color }));
  }

  onNameChange(value: string): void {
    this.formModel.update((m) => ({ ...m, name: value ?? '' }));
  }

  onDescriptionChange(value: string): void {
    this.formModel.update((m) => ({ ...m, description: value ?? '' }));
  }

  onColorInputChange(value: string): void {
    const next = (value ?? '').trim();
    this.formModel.update((m) => ({ ...m, color: next.length > 0 ? next : DEFAULT_AREA_COLOR }));
  }

  onTabChange(value: Tab): void {
    // Switching away from Draw unmounts the mapbox-map component (its container is inside
    // the @if branch). Hold onto the previous reference would emit through a destroyed
    // OutputRef (NG0953) the next time we tried to fit it; clear it instead and let
    // `onMapLoad` re-bind when the new map instance mounts.
    if (this.activeTab() === 'draw' && value !== 'draw') {
      this.mapInstance = null;
    }
    this.activeTab.set(value);
  }

  toggleAdvanced(): void {
    this.advancedOpen.update((open) => !open);
  }

  // ─── Draw tab ──────────────────────────────────────────────────────────
  setDrawMode(mode: DrawMode): void {
    this.drawMode.set(mode);
  }

  clearDrawing(): void {
    this.drawnVertices.set([]);
    this.uploadedFeature.set(null);
    this.uploadedFile.set(null);
    this.uploadError.set(null);
  }

  // Custom zoom buttons stacked at the bottom of the drawing toolbar — Mapbox's stock
  // NavigationControl would otherwise paint its own +/- at top-right and overlap our
  // pencil/edit/trash group. Routing through Mapbox's animated zoomIn/zoomOut means we
  // get the same eased motion as scroll-wheel zoom.
  zoomIn(): void {
    this.mapInstance?.zoomIn();
  }

  zoomOut(): void {
    this.mapInstance?.zoomOut();
  }

  onMapLoad(map: MapboxMap): void {
    this.mapInstance = map;
    requestAnimationFrame(() => map.resize());
    this.installMapSources(map);
    if (this.previewFeature()) {
      this.fitMapToFeature(map, this.previewFeature() as GeoJsonFeature);
    }
    this.bindMapInteractions(map);
  }

  private bindMapInteractions(map: MapboxMap): void {
    // Mapbox emits 'click' even after a 'dblclick', so disable the built-in zoom on dblclick
    // and route it to our finish-drawing handler instead.
    map.doubleClickZoom.disable();

    if (this.mapboxClickListener) map.off('click', this.mapboxClickListener as never);
    this.mapboxClickListener = (event) => this.onMapClick(event);
    map.on('click', this.mapboxClickListener as never);

    if (this.mapboxDoubleClickListener) {
      map.off('dblclick', this.mapboxDoubleClickListener as never);
    }
    this.mapboxDoubleClickListener = () => this.finishDrawing();
    map.on('dblclick', this.mapboxDoubleClickListener as never);

    if (this.mapboxMouseDownListener) {
      map.off('mousedown', this.mapboxMouseDownListener as never);
    }
    this.mapboxMouseDownListener = (event) => this.onMapMouseDown(event);
    map.on('mousedown', 'area-polygon-vertices', this.mapboxMouseDownListener as never);
  }

  private onMapClick(event: MapMouseEvent): void {
    if (this.drawMode() !== 'add') return;
    // Click on the first vertex closes the polygon.
    if (this.drawnVertices().length >= 3 && this.isClickOnFirstVertex(event)) {
      this.finishDrawing();
      return;
    }
    // Once a polygon has been imported, clicks restart drawing from scratch — feels safer
    // than silently editing imported geometry.
    if (this.uploadedFeature()) {
      this.uploadedFeature.set(null);
    }
    this.drawnVertices.update((v) => [...v, [event.lngLat.lng, event.lngLat.lat]]);
  }

  /**
   * Pixel-space hit test against the first vertex. The Mapbox layer would let us query
   * features under the cursor too, but vertex points are sized in pixels so a plain
   * distance check is simpler and more reliable across zoom levels.
   */
  private isClickOnFirstVertex(event: MapMouseEvent): boolean {
    const map = this.mapInstance;
    const firstVertex = this.drawnVertices().at(0);
    if (!map || !firstVertex) return false;
    const projected = map.project({ lng: firstVertex[0], lat: firstVertex[1] });
    const dx = event.point.x - projected.x;
    const dy = event.point.y - projected.y;
    // The vertex halo radius from area-polygon-vertices is 9px; allow 12px for fat fingers.
    return dx * dx + dy * dy <= 12 * 12;
  }

  private finishDrawing(): void {
    // The polygon is already a valid ring at this point — buildPolygonFeatureFromVertices
    // closes the loop when we hand the feature to mapbox-gl. Nothing to do beyond locking
    // any future clicks against the geometry; we already gate that on drawMode.
    if (this.drawnVertices().length < 3) return;
    this.setDrawMode('edit');
  }

  private onMapMouseDown(event: MapMouseEvent): void {
    if (this.drawMode() !== 'edit') return;
    const map = this.mapInstance;
    if (!map) return;
    const features = map.queryRenderedFeatures(event.point, { layers: ['area-polygon-vertices'] });
    const first = features[0];
    if (!first) return;
    const idx = (first.properties as { index?: number } | null | undefined)?.index;
    if (typeof idx !== 'number') return;
    this.dragVertexIndex = idx;
    event.preventDefault();
    map.getCanvas().style.cursor = 'grabbing';
    map.on('mousemove', this.onVertexDrag);
    map.once('mouseup', this.onVertexDragEnd);
  }

  private onVertexDrag = (event: MapMouseEvent): void => {
    const idx = this.dragVertexIndex;
    if (idx === null) return;
    this.drawnVertices.update((v) => {
      const next = v.slice();
      next[idx] = [event.lngLat.lng, event.lngLat.lat];
      return next;
    });
  };

  private onVertexDragEnd = (): void => {
    const map = this.mapInstance;
    if (map) {
      map.off('mousemove', this.onVertexDrag);
      map.getCanvas().style.cursor = '';
    }
    this.dragVertexIndex = null;
  };

  private installMapSources(map: MapboxMap): void {
    const color = this.formModel().color;
    if (!map.getSource('area-polygon')) {
      map.addSource('area-polygon', {
        type: 'geojson',
        data: this.mapboxSourceData() as never,
      });
    }
    if (!map.getSource('area-polygon-vertices')) {
      map.addSource('area-polygon-vertices', {
        type: 'geojson',
        data: this.vertexSourceData() as never,
      });
    }
    if (!map.getLayer('area-polygon-fill')) {
      map.addLayer({
        id: 'area-polygon-fill',
        type: 'fill',
        source: 'area-polygon',
        paint: { 'fill-color': color, 'fill-opacity': 0.25 },
      });
    }
    if (!map.getLayer('area-polygon-stroke')) {
      map.addLayer({
        id: 'area-polygon-stroke',
        type: 'line',
        source: 'area-polygon',
        paint: { 'line-color': color, 'line-width': 2.4 },
      });
    }
    if (!map.getLayer('area-polygon-vertices')) {
      map.addLayer({
        id: 'area-polygon-vertices',
        type: 'circle',
        source: 'area-polygon-vertices',
        paint: {
          'circle-radius': 6,
          'circle-color': '#ffffff',
          'circle-stroke-color': color,
          'circle-stroke-width': 2,
        },
      });
    }
  }

  private applyDataToMap(
    data: GeoJsonFeature | { type: 'FeatureCollection'; features: [] },
    vertices: { type: 'FeatureCollection'; features: object[] }
  ): void {
    const map = this.mapInstance;
    if (!map?.isStyleLoaded()) return;
    const polyRaw = map.getSource('area-polygon') as GeoJSONSource | undefined;
    polyRaw?.setData(data as never);
    const vertexRaw = map.getSource('area-polygon-vertices') as GeoJSONSource | undefined;
    vertexRaw?.setData(vertices as never);
  }

  private applyColorToMap(color: string): void {
    const map = this.mapInstance;
    if (!map?.isStyleLoaded()) return;
    if (map.getLayer('area-polygon-fill')) {
      map.setPaintProperty('area-polygon-fill', 'fill-color', color);
    }
    if (map.getLayer('area-polygon-stroke')) {
      map.setPaintProperty('area-polygon-stroke', 'line-color', color);
    }
    if (map.getLayer('area-polygon-vertices')) {
      map.setPaintProperty('area-polygon-vertices', 'circle-stroke-color', color);
    }
  }

  private fitMapToFeature(map: MapboxMap, feature: GeoJsonFeature): void {
    const bounds = this.featureBounds(feature);
    if (bounds) map.fitBounds(bounds, { padding: 40, maxZoom: 14 });
  }

  private featureBounds(feature: GeoJsonFeature): LngLatBoundsLike | null {
    const positions: [number, number][] = [];
    const ringsToWalk =
      feature.geometry.type === 'Polygon'
        ? feature.geometry.coordinates
        : feature.geometry.coordinates.flat();
    for (const ring of ringsToWalk) {
      for (const position of ring) positions.push(position);
    }
    const firstPosition = positions[0];
    if (!firstPosition) return null;
    let minLon = firstPosition[0];
    let maxLon = firstPosition[0];
    let minLat = firstPosition[1];
    let maxLat = firstPosition[1];
    for (const [lon, lat] of positions) {
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
    return [
      [minLon, minLat],
      [maxLon, maxLat],
    ];
  }

  // ─── Upload tab ────────────────────────────────────────────────────────
  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(true);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) this.consumeUploadedFile(file);
  }

  onFilePickerChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) this.consumeUploadedFile(file);
    (event.target as HTMLInputElement).value = '';
  }

  /** Discards the failed upload so the zone returns to its default empty state. */
  dismissUploadError(): void {
    this.uploadError.set(null);
    this.uploadedFile.set(null);
  }

  /** Returns the localised message for whichever validation code the user just hit, if any. */
  uploadErrorMessage(): string | null {
    const error = this.uploadError();
    if (!error) return null;
    const key = `SETTINGS_AREAS.UPLOAD.ERROR.${error.code.toUpperCase().replaceAll('-', '_')}`;
    return this.translateService.instant(key, error);
  }

  private async consumeUploadedFile(file: File): Promise<void> {
    this.uploadError.set(null);
    this.uploadedFile.set({ name: file.name, size: file.size });
    if (file.size > MAX_AREA_UPLOAD_BYTES) {
      this.uploadedFeature.set(null);
      this.uploadError.set({ code: 'invalid-json' });
      return;
    }
    try {
      const text = await file.text();
      const parsed = parseAreaGeoJsonString(text);
      this.uploadedFeature.set(parsed.normalisedFeature);
      this.drawnVertices.set([]);
      // Bring the user back to the map so they can confirm the import landed correctly.
      this.activeTab.set('draw');
      this.setDrawMode('edit');
      const map = this.mapInstance;
      if (map) {
        // Style may still be loading on first open — defer until the next animation frame.
        requestAnimationFrame(() => this.fitMapToFeature(map, parsed.normalisedFeature));
      }
    } catch (e: unknown) {
      this.uploadedFeature.set(null);
      this.uploadError.set(isGeoJsonValidationError(e) ? e : { code: 'invalid-json' });
    }
  }

  // ─── Zip-range editor (Advanced) ─────────────────────────────────────
  /**
   * Whether the "Add zip range" button is enabled. Requires either a country detected via
   * geocoding OR at least one existing row whose country the new row can copy from. This
   * lets the user keep adding rows for a country we know about even if they edited the
   * detected country to something we don't auto-resolve.
   */
  canAddZipRange(): boolean {
    if (this.derivedCountryIso3()) return true;
    return this.formModel().zipRanges.some((r) => (r.country ?? '').trim().length > 0);
  }

  /**
   * Appends an empty range row prefilled with the best-guess country. Preference order:
   *   1. The *last* row's country — once a user picks a non-default country (e.g. switches
   *      from AUT to DEU), additional rows almost always belong to the same country.
   *   2. The reverse-geocoded country from the polygon centroid.
   *   3. Blank, leaving the user to pick from the dropdown.
   * `from` / `to` start blank so the user fills them in via the per-row inputs.
   */
  addZipRange(): void {
    const ranges = this.formModel().zipRanges;
    const lastCountry = (ranges.at(-1)?.country ?? '').trim();
    // Explicit empty-string checks (rather than `||`) — both `lastCountry` and the derived
    // country can legitimately be empty strings when nothing has been resolved yet, and we
    // want to fall through in those cases rather than treating them as "use this value".
    const fallbackCountry = lastCountry !== '' ? lastCountry : (this.derivedCountryIso3() ?? '');
    this.formModel.update((m) => ({
      ...m,
      zipRanges: [...m.zipRanges, { country: fallbackCountry, from: '', to: '' }],
    }));
  }

  removeZipRange(index: number): void {
    this.formModel.update((m) => ({
      ...m,
      zipRanges: m.zipRanges.filter((_, i) => i !== index),
    }));
  }

  updateZipRangeCountry(index: number, value: string): void {
    // Backend requires uppercase 3-letter ISO codes — normalise as the user types so the
    // shape we store always matches the wire format, even when they paste "aut" or "Aut".
    this.replaceZipRange(index, { country: (value ?? '').toUpperCase().slice(0, 3) });
  }

  updateZipRangeFrom(index: number, value: string): void {
    this.replaceZipRange(index, { from: value ?? '' });
  }

  updateZipRangeTo(index: number, value: string): void {
    this.replaceZipRange(index, { to: value ?? '' });
  }

  private replaceZipRange(index: number, patch: Partial<ZipRange>): void {
    this.formModel.update((m) => {
      const next = m.zipRanges.slice();
      const current = next[index];
      if (!current) return m;
      next[index] = { ...current, ...patch };
      return { ...m, zipRanges: next };
    });
  }

  // ─── Submit / cancel ───────────────────────────────────────────────────
  submit(): void {
    const feature = this.previewFeature();
    if (!feature) return;
    const model = this.formModel();
    const trimmed: AreaFormResult = {
      name: model.name.trim(),
      description: nullableTrim(model.description),
      color: model.color,
      zipRanges: [...model.zipRanges],
      geoJson: feature,
    };
    this.save.emit(trimmed);
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onDialogVisibilityChange(value: boolean): void {
    this.visibleChange.emit(value);
  }

  // ─── State helpers ─────────────────────────────────────────────────────
  private hydrateFromArea(area: Area): void {
    this.formModel.set({
      name: area.name ?? '',
      description: area.description ?? '',
      color: area.color ?? DEFAULT_AREA_COLOR,
      zipRanges: area.zipRanges ? area.zipRanges.map((r) => ({ ...r })) : [],
    });
    this.serverFieldErrors.set({});
    this.drawnVertices.set([]);
    this.uploadedFile.set(null);
    this.uploadError.set(null);
    if (area.geoJson) {
      try {
        const parsed = parseAreaGeoJsonString(JSON.stringify(area.geoJson));
        this.uploadedFeature.set(parsed.normalisedFeature);
        this.setDrawMode('edit');
        const map = this.mapInstance;
        if (map) requestAnimationFrame(() => this.fitMapToFeature(map, parsed.normalisedFeature));
      } catch {
        // Existing area has an unparseable polygon (shouldn't happen for backend-emitted data,
        // but defensively reset rather than crash the dialog).
        this.uploadedFeature.set(null);
      }
    } else {
      this.uploadedFeature.set(null);
    }
    this.activeTab.set('draw');
    this.advancedOpen.set(false);
  }

  private resetForm(): void {
    this.formModel.set({
      name: '',
      description: '',
      color: DEFAULT_AREA_COLOR,
      zipRanges: [],
    });
    this.serverFieldErrors.set({});
    this.drawnVertices.set([]);
    this.uploadedFeature.set(null);
    this.uploadedFile.set(null);
    this.uploadError.set(null);
    this.activeTab.set('draw');
    this.setDrawMode('add');
    this.advancedOpen.set(false);
  }
}

function nullableTrim(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * Map a JSON Pointer from the backend's ProblemDetail.errors[].pointer to the field name
 * we render inline. Anything that doesn't match returns `null` and the caller treats it
 * as a general (toast-worthy) error.
 */
function routePointer(pointer: string | undefined): string | null {
  if (!pointer) return null;
  const path = pointer.startsWith('/') ? pointer.slice(1) : pointer;
  if (path === 'name' || path === 'title') return 'name';
  if (path === 'description') return 'description';
  if (path === 'color') return 'color';
  if (path === 'geo_json' || path === 'geoJson') return 'geometry';
  if (path.startsWith('zip_ranges') || path.startsWith('zipRanges')) return 'zipRanges';
  return null;
}

/** Best-effort human-readable message extracted from a single ProblemDetail sub-error. */
function pickMessage(error: ServerError): string {
  return error.detail ?? error.title ?? '';
}

/**
 * Naïve centroid of the first ring of a GeoJSON Polygon / MultiPolygon — average of the
 * non-closing vertices. Enough to drop the geocoder somewhere inside the shape; we don't
 * need a true centroid for "which country does this fall in".
 */
function polygonCentroid(feature: GeoJsonFeature): { lat: number; lon: number } | null {
  const ring =
    feature.geometry.type === 'Polygon'
      ? feature.geometry.coordinates[0]
      : feature.geometry.coordinates[0]?.[0];
  if (!ring || ring.length < 3) return null;
  // Drop the closing duplicate (a valid LinearRing has the first vertex repeated last).
  const last = ring.at(-1);
  const first = ring.at(0);
  const vertices =
    last && first && last[0] === first[0] && last[1] === first[1] ? ring.slice(0, -1) : ring;
  if (vertices.length === 0) return null;
  let lonSum = 0;
  let latSum = 0;
  for (const [lon, lat] of vertices) {
    lonSum += lon;
    latSum += lat;
  }
  return { lat: latSum / vertices.length, lon: lonSum / vertices.length };
}
